import { useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { AgreementConsent } from "@/components/auth/AgreementConsent";
import { agreementLead, agreementTitle } from "@/lib/agreements";
import { acceptAgreements, agreementDocumentUrl, logout } from "@/lib/api";
import type { PendingAgreement } from "@/stores/useAgreementAcceptanceStore";

/**
 * 未同意の文書があるとき、画面全体を差し替えて 1 文書ずつ順に同意を求める。
 * 最後の文書で「同意して続ける」を押すとまとめて送り、元の画面を読み込み直す。
 */
export function AgreementAcceptancePage({ agreements }: { agreements: PendingAgreement[] }) {
	const [step, setStep] = useState(0);
	// 「次へ」はチェックしないと押せないので、チェック済みの文書は先頭から連続する。その最後の番号。
	const [checkedUpTo, setCheckedUpTo] = useState(-1);
	const [submitting, setSubmitting] = useState(false);
	const [loggingOut, setLoggingOut] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const current = agreements[step];
	const title = agreementTitle(current.id);
	const isFirst = step === 0;
	const isLast = step === agreements.length - 1;
	const checked = step <= checkedUpTo;

	async function handleAccept() {
		setSubmitting(true);
		setError(null);
		try {
			await acceptAgreements(agreements);
			window.location.reload();
		} catch (err) {
			console.error("同意の送信に失敗しました", err);
			setError(err instanceof Error ? err.message : "同意に失敗しました。");
			setSubmitting(false);
		}
	}

	return (
		<div
			data-testid="agreement-acceptance-page"
			style={{
				minHeight: "100vh",
				background: "var(--surface-2)",
				display: "grid",
				placeItems: "center",
				fontFamily: "var(--font-sans)",
				padding: 24,
			}}
		>
			<div
				style={{
					width: "100%",
					maxWidth: 720,
					background: "var(--surface-1)",
					borderRadius: "var(--radius-5)",
					boxShadow: "var(--shadow-card)",
					padding: 32,
					display: "flex",
					flexDirection: "column",
					gap: 20,
				}}
			>
				<header style={{ display: "flex", flexDirection: "column", gap: 4 }}>
					<h1
						style={{
							margin: 0,
							fontSize: "var(--text-display)",
							fontWeight: 600,
							lineHeight: "var(--leading-snug)",
							letterSpacing: "var(--tracking-display)",
							color: "var(--fg-1)",
						}}
					>
						{title}への同意
						{agreements.length > 1 && (
							<span style={{ marginLeft: 8, fontSize: "var(--text-ui)", color: "var(--fg-3)" }}>
								({step + 1}/{agreements.length})
							</span>
						)}
					</h1>
					<p
						style={{
							margin: 0,
							fontSize: "var(--text-ui)",
							color: "var(--fg-2)",
							lineHeight: "var(--leading-ui)",
						}}
					>
						{agreementLead(current.id)}
					</p>
				</header>

				<AgreementConsent
					key={current.id}
					documentUrl={agreementDocumentUrl(current.id)}
					label={`${title}に同意します`}
					checked={checked}
					onCheckedChange={(value) => setCheckedUpTo(value ? step : step - 1)}
				/>

				{error && (
					<p
						style={{
							margin: 0,
							fontSize: "var(--text-caption)",
							color: "var(--danger)",
							lineHeight: "var(--leading-ui)",
						}}
					>
						{error}
					</p>
				)}

				<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
					<ConfirmButton
						type="button"
						variant="outline"
						label={isFirst ? "ログアウト" : "戻る"}
						loading={isFirst && loggingOut}
						disabled={submitting}
						onClick={() => {
							if (!isFirst) return setStep(step - 1);
							setLoggingOut(true);
							void logout();
						}}
						data-testid={isFirst ? "agreement-acceptance-logout" : "agreement-acceptance-back"}
					/>
					<ConfirmButton
						type="button"
						label={isLast ? "同意して続ける" : "次へ"}
						loading={isLast && submitting}
						disabled={!checked}
						onClick={() => (isLast ? void handleAccept() : setStep(step + 1))}
						data-testid={isLast ? "agreement-acceptance-submit" : "agreement-acceptance-next"}
					/>
				</div>
			</div>
		</div>
	);
}
